import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import Marquee from "@/components/Marquee";
import { NewsletterSection } from "@/components/NewsletterForm";
import { PageIntro, FadeUp } from "@/components/Reveal";
import { PatchBadge } from "@/components/Doodles";
import { useContent } from "@/context/ContentContext";

const WhatIsPcch = () => {
    const { t } = useContent();
    const features = t("whatis.features", []);
    return (
        <>
            <Seo
                title="What's Inside?"
                description="Panther City Clubhouse is a clubhouse and third space in Richland Hills, TX — a warm, creative, slightly weird spot to gather, create, learn, work, host, and collaborate."
            />
            <PageIntro kicker="great question, glad you asked" title={t("whatis.headline", "STEP INSIDE").toUpperCase()} intro={t("whatis.intro", "")} />
            <section data-testid="whatis-features" className="px-5 pb-20 sm:px-10">
                <div className="mx-auto max-w-6xl space-y-16 sm:space-y-24">
                    {features.map((f, i) => (
                        <FadeUp key={f.name} delay={0.05}>
                            {f.special ? (
                                <div
                                    data-testid={`whatis-feature-${i}`}
                                    className="border-2 border-dashed border-cream/70 bg-brick p-8 text-cream hard-shadow-lg rotate-[0.5deg] sm:p-12"
                                >
                                    <div className="flex flex-wrap items-start justify-between gap-8">
                                        <div className="max-w-2xl">
                                            <span className="inline-block -rotate-3 border-2 border-ink bg-mustard px-3 py-1 font-display text-xs tracking-[0.2em] text-ink hard-shadow">
                                                {f.badge || "SPACE AVAILABLE — APPLY WITHIN"}
                                            </span>
                                            <h2 className="mt-4 font-display text-3xl tracking-wide sm:text-4xl">{f.name}</h2>
                                            <p className="mt-4 leading-relaxed text-cream/85">{f.body}</p>
                                            {f.link ? (
                                                f.link.startsWith("/") ? (
                                                    <Link to={f.link} data-testid={`whatis-special-cta-${i}`} className="btn-stamp mt-6 inline-block !bg-mustard !text-ink">
                                                        {f.link_label || "Learn More"} →
                                                    </Link>
                                                ) : (
                                                    <a href={f.link} target="_blank" rel="noreferrer" data-testid={`whatis-special-cta-${i}`} className="btn-stamp mt-6 inline-block !bg-mustard !text-ink">
                                                        {f.link_label || "Learn More"} →
                                                    </a>
                                                )
                                            ) : null}
                                        </div>
                                        {f.photo ? (
                                            <img
                                                src={f.photo}
                                                alt={f.name}
                                                loading="lazy"
                                                data-testid={`whatis-special-photo-${i}`}
                                                className="w-40 shrink-0 rotate-3 border-2 border-ink object-cover object-[50%_15%] hard-shadow sm:w-52"
                                            />
                                        ) : (
                                            <PatchBadge className="h-24 w-24 shrink-0 rotate-6 text-forest sm:h-32 sm:w-32" />
                                        )}
                                    </div>
                                </div>
                            ) : (
                                <div data-testid={`whatis-feature-${i}`} className="grid items-center gap-8 lg:grid-cols-2 lg:gap-14">
                                    <div className={i % 2 ? "lg:order-2" : ""}>
                                        <div className={`relative border-2 border-ink bg-paper p-3 hard-shadow-lg ${i % 2 ? "rotate-1" : "-rotate-1"}`}>
                                            <span className="tape-strip" aria-hidden="true" />
                                            <img
                                                src={f.photo}
                                                alt={f.name}
                                                loading="lazy"
                                                className="aspect-[4/3] w-full border-2 border-ink object-cover sepia-[0.25] saturate-[0.85]"
                                            />
                                        </div>
                                    </div>
                                    <div className={i % 2 ? "lg:order-1" : ""}>
                                        <h2 className="font-display text-3xl tracking-wide text-ink sm:text-4xl">{f.name}</h2>
                                        <p className="mt-4 leading-relaxed text-ink/75">{f.body}</p>
                                    </div>
                                </div>
                            )}
                        </FadeUp>
                    ))}
                </div>
                <FadeUp className="mx-auto mt-20 max-w-6xl">
                    <div className="flex flex-col items-start gap-6 border-2 border-dashed border-ink/40 p-7 sm:flex-row sm:items-center sm:justify-between sm:p-9">
                        <p className="max-w-xl font-display text-2xl leading-tight tracking-wide text-ink sm:text-3xl">
                            {t("whatis.partner_note", "Interested in becoming our next in-house partner?")}
                        </p>
                        <Link to="/get-involved" data-testid="whatis-partner-cta" className="btn-stamp !bg-brick !text-xl">
                            {t("whatis.partner_cta", "Let's Talk")}
                        </Link>
                    </div>
                </FadeUp>
            </section>
            <Marquee />
            <NewsletterSection idPrefix="whatis-newsletter" tone="forest" />
        </>
    );
};

export default WhatIsPcch;
